import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiArrowLeft, FiUser, FiMail, FiLink, FiMusic,
  FiImage, FiGlobe, FiCheck, FiLoader, FiAlertCircle,
} = FiIcons;

const SUBMISSIONS_EMAIL = 'submissions@visionmedia345.com';

const buildEmailBody = (d) => [
  'NEW CREATOR APPLICATION — Vision Direct',
  '',
  `Full Name:         ${d.display_name || 'N/A'}`,
  `Email:             ${d.email || 'N/A'}`,
  `Role Applied For:  ${d.requested_role?.replace(/_/g, ' ').toUpperCase() || 'N/A'}`,
  `Bio:               ${d.bio || 'N/A'}`,
  '',
  '--- Reason for Access ---',
  d.reason || 'Not provided',
  '',
  d.experience ? `--- Experience ---\n${d.experience}\n` : '',
  `Spotify:    ${d.social_spotify    || 'Not provided'}`,
  `Instagram:  ${d.social_instagram  || 'Not provided'}`,
  `SoundCloud: ${d.social_soundcloud || 'Not provided'}`,
  `Website:    ${d.website           || 'Not provided'}`,
  '',
  'Submitted via Vision Direct Platform',
].filter(l => l !== null && l !== undefined).join('\n');

// Called SYNCHRONOUSLY inside the click handler — before any await
const triggerEmail = (formData) => {
  try {
    const subject = encodeURIComponent(
      `[Creator Application] ${formData.requested_role?.replace(/_/g, ' ').toUpperCase() || 'Creator'} — ${formData.display_name || formData.email}`
    );
    const body = encodeURIComponent(buildEmailBody(formData));
    const a = document.createElement('a');
    a.href = `mailto:${SUBMISSIONS_EMAIL}?subject=${subject}&body=${body}`;
    a.target = '_blank';
    a.style.cssText = 'position:fixed;opacity:0;pointer-events:none;';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch (_) {} }, 1000);
  } catch (err) {
    console.error('Email trigger failed:', err);
  }
};

const AccessRequestForm = ({ user, onSubmit, loading, error }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    display_name: '',
    requested_role: 'artist',
    bio: '',
    social_spotify: '',
    social_instagram: '',
    social_soundcloud: '',
    website: '',
  });
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // onClick handler — NOT async — so email fires within user gesture
  const handleSubmitClick = (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!formData.display_name.trim()) {
      setSubmitError('Please enter your artist/display name');
      return;
    }
    if (!formData.email.trim()) {
      setSubmitError('Please enter your email address');
      return;
    }

    // ⚡ Fire email SYNCHRONOUSLY — still within the user gesture stack
    triggerEmail(formData);

    // Now hand off to the async parent handler
    onSubmit(formData).catch(err => {
      setSubmitError(err.message || 'Failed to submit application');
    });
  };

  const nextStep = () => {
    if (step === 1 && !formData.email.trim()) { setSubmitError('Please enter your email address'); return; }
    if (step === 2 && !formData.display_name.trim()) { setSubmitError('Please enter your artist/display name'); return; }
    setSubmitError('');
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-6 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink w-[600px] h-[600px] top-[-20%] right-[-15%] animate-caustic" />
        <div className="ambient-orb-coral w-[500px] h-[500px] bottom-[-15%] left-[-10%] animate-caustic" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 w-full max-w-2xl space-y-6">
        <button onClick={() => navigate('/')}
          className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group w-fit">
          <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center group-hover:border-brand-pink/30 transition-all">
            <SafeIcon icon={FiArrowLeft} className="text-sm" />
          </div>
          <span className="text-xs font-black uppercase tracking-widest">Home</span>
        </button>

        <div className="glass-strong rounded-[2.5rem] p-10 space-y-8">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-brand-pink/20 border border-brand-pink/40 flex items-center justify-center mx-auto">
              <SafeIcon icon={FiUser} className="text-3xl text-brand-pink" />
            </div>
            <h1 className="text-2xl font-black uppercase tracking-tighter text-white">Creator Application</h1>
            <p className="text-zinc-500 font-bold text-sm">Join Vision Direct as a verified creator</p>
          </div>

          <div className="flex items-center gap-3">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={`flex-1 h-2 rounded-full ${step >= s ? 'bg-brand-pink' : 'bg-zinc-800'}`} />
                <span className={`text-xs font-black uppercase tracking-widest ${step >= s ? 'text-brand-pink' : 'text-zinc-600'}`}>
                  {s === 1 ? 'Role' : s === 2 ? 'Profile' : 'Socials'}
                </span>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Email Address</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required
                      placeholder="you@example.com"
                      className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 pl-11 pr-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-brand-pink/40 focus:outline-none transition-all" />
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Select Role</label>
                  <div className="grid grid-cols-1 gap-3">
                    {[
                      { value: 'artist', label: 'Artist', desc: 'Release music, sell directly to fans', icon: FiMusic },
                      { value: 'label_admin', label: 'Label Admin', desc: 'Manage multiple artists and releases', icon: FiUser },
                    ].map(role => (
                      <button key={role.value} type="button"
                        onClick={() => setFormData(prev => ({ ...prev, requested_role: role.value }))}
                        className={`p-5 rounded-2xl border-2 text-left transition-all ${
                          formData.requested_role === role.value
                            ? 'bg-brand-pink/10 border-brand-pink'
                            : 'bg-zinc-900/60 border-white/5 hover:border-white/10'
                        }`}>
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${formData.requested_role === role.value ? 'bg-brand-pink/20' : 'bg-zinc-800'}`}>
                            <SafeIcon icon={role.icon} className={`text-xl ${formData.requested_role === role.value ? 'text-brand-pink' : 'text-zinc-500'}`} />
                          </div>
                          <div className="space-y-1">
                            <h3 className={`text-sm font-black uppercase tracking-widest ${formData.requested_role === role.value ? 'text-brand-pink' : 'text-white'}`}>{role.label}</h3>
                            <p className="text-xs font-bold text-zinc-500">{role.desc}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Artist/Display Name *</label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                    <input type="text" name="display_name" value={formData.display_name} onChange={handleChange} required
                      placeholder="Your stage name or display name"
                      className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 pl-11 pr-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-brand-pink/40 focus:outline-none transition-all" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Bio</label>
                  <textarea name="bio" value={formData.bio} onChange={handleChange}
                    placeholder="Tell us about yourself, your music, and your vision..."
                    rows={5}
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl p-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-brand-pink/40 focus:outline-none transition-all resize-none" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiLink} className="text-sm" /> Spotify (Optional)
                  </label>
                  <input type="url" name="social_spotify" value={formData.social_spotify} onChange={handleChange}
                    placeholder="https://open.spotify.com/artist/..."
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-brand-pink/40 focus:outline-none transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiImage} className="text-sm" /> Instagram (Optional)
                  </label>
                  <input type="url" name="social_instagram" value={formData.social_instagram} onChange={handleChange}
                    placeholder="https://instagram.com/yourname"
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-brand-pink/40 focus:outline-none transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiMusic} className="text-sm" /> SoundCloud (Optional)
                  </label>
                  <input type="url" name="social_soundcloud" value={formData.social_soundcloud} onChange={handleChange}
                    placeholder="https://soundcloud.com/yourname"
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-brand-pink/40 focus:outline-none transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiGlobe} className="text-sm" /> Website (Optional)
                  </label>
                  <input type="url" name="website" value={formData.website} onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-brand-pink/40 focus:outline-none transition-all" />
                </div>
              </div>
            )}

            {(error || submitError) && (
              <div className="flex items-start gap-3 p-4 bg-red-500/8 border border-red-500/20 rounded-2xl">
                <SafeIcon icon={FiAlertCircle} className="text-red-400 text-sm flex-shrink-0 mt-0.5" />
                <p className="text-xs font-bold text-red-400">{error || submitError}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              {step > 1 && (
                <button type="button" onClick={prevStep}
                  className="flex-1 py-4 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all">
                  Back
                </button>
              )}
              {step < 3 ? (
                <button type="button" onClick={nextStep}
                  className="flex-1 py-4 btn-brand rounded-2xl text-xs font-black uppercase tracking-widest">
                  Continue
                </button>
              ) : (
                <button type="button" onClick={handleSubmitClick} disabled={loading}
                  className="flex-1 py-4 btn-brand rounded-2xl text-xs font-black uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {loading
                    ? <><SafeIcon icon={FiLoader} className="animate-spin text-base" /> Submitting…</>
                    : <><SafeIcon icon={FiCheck} /> Submit Application</>
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessRequestForm;